import React, { type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader,
  TextField,
  Select,
  Accordion,
  AccordionTrigger,
  AccordionContent
} from '@deque/cauldron-react';

import type { MappedIntegrationField } from '../../ShareToIntegrationModal';
import type { ErrorOrNull } from '../../../../../../../typings/utils';
import styles from './SetValues.css';
import { IssueFieldSource } from '../../../../../utils/integrations-client/jira/fieldMapping/types';

export interface SetValuesProps {
  capitalizedIntegrationName: string;
  integrationFields: MappedIntegrationField[];
  setIntegrationFields: Dispatch<SetStateAction<MappedIntegrationField[]>>;
  loading: boolean;
  error: ErrorOrNull;
  fieldRefs: React.MutableRefObject<
    (HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null)[]
  >;
}

export const isSelectField = (type: string, items?: string) => {
  return type === 'option' || (type === 'array' && items !== 'string');
};

export const getInputType = (type: string) => {
  if (type === 'string' || type === 'array') {
    return 'text';
  }
  if (type === 'number') {
    return 'number';
  }
  if (type === 'date') {
    return 'date';
  }
  if (type === 'datetime') {
    return 'datetime-local';
  }
  return 'text';
};

const adjustDateTimeString = (input: string): string => {
  const firstIndex = input.indexOf(':');

  if (firstIndex === -1) {
    return input;
  }

  const secondIndex = input.indexOf(':', firstIndex + 1);
  if (secondIndex === -1) {
    return input;
  }

  return input.substring(0, secondIndex);
};

export const formatDateValue = (
  timestamp: number | Date,
  type: 'date' | 'datetime'
): string => {
  const date = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
  return type === 'date'
    ? date.toISOString().slice(0, 10) // Returns 'YYYY-MM-DD'
    : date.toISOString().slice(0, 19); // Returns 'YYYY-MM-DDTHH:MM'
};

const SetValues = ({
  capitalizedIntegrationName,
  integrationFields,
  setIntegrationFields,
  loading,
  error,
  fieldRefs
}: SetValuesProps) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={styles.setValuesContainer}>
        <Loader label={t('Loading...')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.setValuesContainer}>
        <p>{t('Error loading fields')}</p>
      </div>
    );
  }

  return (
    <div className={styles.setValuesContainer}>
      <Accordion open={true}>
        <AccordionTrigger>Required Fields</AccordionTrigger>
        <AccordionContent>
          {integrationFields
            .filter(integrationField => integrationField.required)
            .map(
              (
                {
                  integrationFieldName,
                  required,
                  error: err,
                  type,
                  items,
                  allowedValues,
                  sourceType,
                  rawAllowedValues
                },
                index
              ) => {
                let calculatedValue = integrationFields.find(
                  v => v.integrationFieldName === integrationFieldName
                )?.axeFieldValue;
                if (
                  sourceType === IssueFieldSource.FixedValue &&
                  rawAllowedValues
                ) {
                  const selectedAllowedValue = rawAllowedValues.find(
                    rawAllowedValue => rawAllowedValue.id === calculatedValue
                  );

                  if (selectedAllowedValue) {
                    calculatedValue = selectedAllowedValue.value;
                  }
                }

                const isSelect = isSelectField(type, items);

                if (isSelect) {
                  return (
                    <Select
                      key={integrationFieldName}
                      label={t(`{{fieldName}} {{ integrationName }} Field`, {
                        fieldName: integrationFieldName
                          .split(' ')
                          .map(
                            word => word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(' '),
                        integrationName: capitalizedIntegrationName
                      })}
                      required={required}
                      error={err}
                      ref={el => (fieldRefs.current[index] = el)}
                      value={calculatedValue}
                      onChange={e => {
                        e.persist();
                        setIntegrationFields(prevValues =>
                          prevValues.map(v =>
                            v.integrationFieldName === integrationFieldName
                              ? {
                                  ...v,
                                  axeFieldValue: e.target.value,
                                  error: undefined
                                }
                              : v
                          )
                        );
                      }}
                      options={[
                        {
                          key: 'choose',
                          label: 'Please choose',
                          value: ''
                        },
                        ...(allowedValues?.map(v => ({
                          key: v,
                          label: v,
                          value: v
                        })) ?? [])
                      ]}
                    />
                  );
                }

                const inputType = getInputType(type);

                let value: string | number | Date =
                  integrationFields.find(
                    v => v.integrationFieldName === integrationFieldName
                  )?.axeFieldValue ?? '';

                if (type === 'date') {
                  value =
                    typeof value === 'string'
                      ? value
                      : formatDateValue(value, type);
                }

                if (type === 'datetime') {
                  value =
                    typeof value === 'string'
                      ? adjustDateTimeString(value)
                      : formatDateValue(value, type);
                }

                return (
                  <TextField
                    className="set-values-field"
                    key={integrationFieldName}
                    data-field-name={integrationFieldName}
                    data-testid={type}
                    required={required}
                    type={inputType}
                    error={err}
                    fieldRef={el => (fieldRefs.current[index] = el)}
                    label={t(`{{fieldName}} {{ integrationName }} Field`, {
                      fieldName: integrationFieldName
                        .split(' ')
                        .map(
                          word => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(' '),
                      integrationName: capitalizedIntegrationName
                    })}
                    value={value}
                    onChange={newValue => {
                      setIntegrationFields(prevValues =>
                        prevValues.map(v =>
                          v.integrationFieldName === integrationFieldName
                            ? {
                                ...v,
                                axeFieldValue: newValue,
                                error: undefined
                              }
                            : v
                        )
                      );
                    }}
                  />
                );
              }
            )}
        </AccordionContent>
      </Accordion>
      <Accordion>
        <AccordionTrigger>All Other Fields</AccordionTrigger>
        <AccordionContent>
          {integrationFields
            .filter(integrationField => !integrationField.required)
            .map(
              (
                {
                  integrationFieldName,
                  required,
                  error: err,
                  type,
                  items,
                  allowedValues,
                  sourceType,
                  rawAllowedValues
                },
                index
              ) => {
                let calculatedValue = integrationFields.find(
                  v => v.integrationFieldName === integrationFieldName
                )?.axeFieldValue;
                if (
                  sourceType === IssueFieldSource.FixedValue &&
                  rawAllowedValues
                ) {
                  const selectedAllowedValue = rawAllowedValues.find(
                    rawAllowedValue => rawAllowedValue.id === calculatedValue
                  );

                  if (selectedAllowedValue) {
                    calculatedValue = selectedAllowedValue.value;
                  }
                }

                const isSelect = isSelectField(type, items);

                if (isSelect) {
                  return (
                    <Select
                      key={integrationFieldName}
                      label={t(`{{fieldName}} {{ integrationName }} Field`, {
                        fieldName: integrationFieldName
                          .split(' ')
                          .map(
                            word => word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(' '),
                        integrationName: capitalizedIntegrationName
                      })}
                      required={required}
                      error={err}
                      ref={el => (fieldRefs.current[index] = el)}
                      value={calculatedValue}
                      onChange={e => {
                        e.persist();
                        setIntegrationFields(prevValues =>
                          prevValues.map(v =>
                            v.integrationFieldName === integrationFieldName
                              ? {
                                  ...v,
                                  axeFieldValue: e.target.value,
                                  error: undefined
                                }
                              : v
                          )
                        );
                      }}
                      options={[
                        {
                          key: 'choose',
                          label: 'Please choose',
                          value: ''
                        },
                        ...(allowedValues?.map(v => ({
                          key: v,
                          label: v,
                          value: v
                        })) ?? [])
                      ]}
                    />
                  );
                }

                const inputType = getInputType(type);

                let value: string | number | Date =
                  integrationFields.find(
                    v => v.integrationFieldName === integrationFieldName
                  )?.axeFieldValue ?? '';

                if (type === 'date') {
                  value =
                    typeof value === 'string'
                      ? value
                      : formatDateValue(value, type);
                }

                if (type === 'datetime') {
                  value =
                    typeof value === 'string'
                      ? adjustDateTimeString(value)
                      : formatDateValue(value, type);
                }

                return (
                  <TextField
                    className="set-values-field"
                    key={integrationFieldName}
                    data-field-name={integrationFieldName}
                    data-testid={type}
                    required={required}
                    type={inputType}
                    error={err}
                    fieldRef={el => (fieldRefs.current[index] = el)}
                    label={t(`{{fieldName}} {{ integrationName }} Field`, {
                      fieldName: integrationFieldName
                        .split(' ')
                        .map(
                          word => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(' '),
                      integrationName: capitalizedIntegrationName
                    })}
                    value={value}
                    onChange={newValue => {
                      setIntegrationFields(prevValues =>
                        prevValues.map(v =>
                          v.integrationFieldName === integrationFieldName
                            ? {
                                ...v,
                                axeFieldValue: newValue,
                                error: undefined
                              }
                            : v
                        )
                      );
                    }}
                  />
                );
              }
            )}
        </AccordionContent>
      </Accordion>
    </div>
  );
};

export default SetValues;
